import { useEffect, useMemo, useRef } from 'react';

import { getENV, useDecisions } from '@libs/common-next';

import { useSearchAnalytics } from '../analytics';
import { createRecentSearches, createSearchCommands } from '../lib';
import { useSearchQueryState } from './use-search-query-state';
import { useSearchResults } from './use-search-results';

export function useSearchCoordinator() {
    const activeRef = useRef(true);
    const queryState = useSearchQueryState();
    const { currentAnalyticsTags, trackApplicationSubmit } = useSearchAnalytics();
    const { ff_ac_click_anx: clickAnalytics, ff_srch_deb: searchDebounceMs } = useDecisions();
    const recentSearchesEnabled = getENV('NEXT_PUBLIC_ENABLE_RECENT_SEARCHES');
    const recentSearches = useMemo(
        () => (recentSearchesEnabled ? createRecentSearches() : null),
        [recentSearchesEnabled]
    );
    const results = useSearchResults({
        analyticsTags: currentAnalyticsTags,
        clickAnalytics,
        query: queryState.query,
        recentSearches,
        searchDebounceMs,
    });
    const commands = createSearchCommands({
        isActive: () => activeRef.current,
        queryState,
        recentSearches,
        results,
        trackApplicationSubmit,
    });

    useEffect(() => {
        activeRef.current = true;
        return () => {
            activeRef.current = false;
        };
    }, []);

    const onQueryChange = (nextQuery: string) => {
        queryState.setQuery(nextQuery);
        results.refresh(nextQuery);
    };

    return {
        clearQuery: queryState.clearQuery,
        completeAssistedSearch: commands.completeAssistedSearch,
        hasVisibleResults: results.hasVisibleResults,
        onInputKeyDown: queryState.onInputKeyDown,
        onQueryChange,
        query: queryState.query,
        refresh: results.refresh,
        reload: results.reload,
        selectSearchResult: commands.selectSearchResult,
        sources: results.sources,
        submitSearch: commands.submitSearch,
    };
}
